import { useEffect, useState } from 'react';
import {
  AuthInputDisabledField,
  DialogModal,
  putClientAccessLinkEmailToClientRequest,
  resetAdvisorUpdatesStatus,
  selectAdvisorUpdatesStatus,
} from '@client';
import { clientUserSignInLinkByEmail, isPlaceholderEmail, isValidEmailFormat } from '@shared';
import { ModalDoneButton } from './Modal';
import { useDispatch, useSelector } from 'react-redux';
import { CopyLinkField } from './CopyLinkField';

type SendResultsLinkModalProps = {
  client: Client;
  open: boolean;
  onClose: () => void;
  successMessage?: string;
};

export function SendResultsLinkModal({
  client,
  open,
  onClose,
  successMessage = 'Access link successfully sent!',
}: SendResultsLinkModalProps) {
  const { clientEmail: rawClientEmail, clientId } = client;
  const clientEmail = isPlaceholderEmail(rawClientEmail) ? undefined : rawClientEmail;
  const advisorStateUpdateKey = 'putClientAccessLinkEmail';
  const status = useSelector(selectAdvisorUpdatesStatus)[advisorStateUpdateKey];
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const invalidEmailErrorMessage = 'Please enter a valid email address.';

  const handleSubmit = () => {
    if (!clientEmail || !isValidEmailFormat(clientEmail)) {
      setErrorMessage(invalidEmailErrorMessage);
      return;
    } else {
      setErrorMessage('');
    }

    dispatch(putClientAccessLinkEmailToClientRequest({ clientId, email: clientEmail }));
  };

  useEffect(() => {
    if (open) {
      setErrorMessage('');
      dispatch(resetAdvisorUpdatesStatus(advisorStateUpdateKey));
    }
  }, [open, clientEmail, dispatch]);

  const signInLink = clientUserSignInLinkByEmail(clientEmail);

  return (
    <DialogModal
      open={open}
      onClose={() => {
        onClose();
        dispatch(resetAdvisorUpdatesStatus(advisorStateUpdateKey));
      }}
      title={'Send Sign-In Link'}
      subTitle={'Send a sign-in link to this client.'}
      width="md"
      errorMessage={
        status === 'error'
          ? 'There was an error sending the results link. Please try again or contact support.'
          : status === 'unsubscribed'
            ? 'The email address you entered is unsubscribed from transaction emails. Please enter a different email address.'
            : errorMessage
      }
      showSuccess={status === 'complete'}
      isLoading={status === 'loading'}
      successMessage={successMessage}
      primaryButton={
        clientEmail ? (
          <ModalDoneButton
            buttonText={'Send Sign-In Link'}
            onClick={handleSubmit}
          />
        ) : undefined
      }
    >
      <div className="my-4">
        {!clientEmail ? (
          <p className="text-base text-gray-700">
            This feauture is currently unavailable for clients without an email on file. Please
            contact support for assistance.
          </p>
        ) : (
          <>
            <AuthInputDisabledField
              label="Client email"
              value={clientEmail}
              id="email"
            />
            <CopyLinkField
              className="mt-4"
              label="Sign-In Link"
              url={signInLink}
              showOpenButton={false}
              tooltipContent={
                <div className="text-sm font-normal">
                  <p>Share this link with your client to allow them to sign in directly.</p>
                </div>
              }
            />
          </>
        )}
      </div>
    </DialogModal>
  );
}
